import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * One hover card for every chart on the tab.
 *
 * Portalled to the body and positioned against the viewport, so a bar at the
 * edge of a clipped panel still gets a card that is fully on screen. It follows
 * the pointer for mouse users; a keyboard focus places it above the element
 * instead, so the same content is reachable without a mouse.
 *
 * Content is data, not markup: a title, an optional line under it, and rows of
 * label/value with an optional colour swatch — every chart describes itself the
 * same way, which is what makes hovering across the tab feel like one product.
 */
export function useChartTooltip() {
  const [tip, setTip] = useState(null);
  const frame = useRef(0);

  const show = useCallback((event, content) => {
    let x;
    let y;
    if (event && "clientX" in event && event.clientX) {
      x = event.clientX;
      y = event.clientY;
    } else if (event?.currentTarget?.getBoundingClientRect) {
      const rect = event.currentTarget.getBoundingClientRect();
      x = rect.left + rect.width / 2;
      y = rect.top;
    } else {
      return;
    }
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => setTip({ x, y, content }));
  }, []);

  const hide = useCallback(() => {
    cancelAnimationFrame(frame.current);
    setTip(null);
  }, []);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  /** Spread onto any hoverable element: mouse, pen and keyboard all show it. */
  const bind = useCallback(
    (content) => ({
      onMouseEnter: (event) => show(event, content),
      onMouseMove: (event) => show(event, content),
      onMouseLeave: hide,
      onFocus: (event) => show(event, content),
      onBlur: hide,
    }),
    [show, hide],
  );

  return { tip, show, hide, bind };
}

export function ChartTooltip({ tip }) {
  const ref = useRef(null);
  const [placement, setPlacement] = useState({ left: 0, top: 0, ready: false });

  useEffect(() => {
    if (!tip || !ref.current) return;
    const { offsetWidth: w, offsetHeight: h } = ref.current;
    const margin = 12;
    let left = tip.x + 14;
    let top = tip.y - h - 14;
    if (left + w + margin > window.innerWidth) left = tip.x - w - 14;
    if (left < margin) left = margin;
    if (top < margin) top = tip.y + 18;
    setPlacement({ left, top, ready: true });
  }, [tip]);

  if (!tip) return null;
  const { title, subtitle, rows = [], footer } = tip.content || {};

  return createPortal(
    <div
      ref={ref}
      className={`mcap-lic-tooltip ${placement.ready ? "is-ready" : ""}`}
      style={{ left: placement.left, top: placement.top }}
      role="tooltip"
    >
      {title && <div className="mcap-lic-tooltip-title">{title}</div>}
      {subtitle && <div className="mcap-lic-tooltip-sub">{subtitle}</div>}
      {rows.length > 0 && (
        <dl className="mcap-lic-tooltip-rows">
          {rows.map((row) => (
            <div key={row.label} className={row.muted ? "is-muted" : ""}>
              <dt>
                {row.color && <span className="mcap-lic-tooltip-dot" style={{ background: row.color }} />}
                {row.label}
              </dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {footer && <div className="mcap-lic-tooltip-foot">{footer}</div>}
    </div>,
    document.body,
  );
}
